import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Tooltip, Badge, Popper, ClickAwayListener } from '@mui/material';
import { Add, Assignment, StickyNote2, Person, Link as LinkIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import CalendarTask from './CalendarTask';
import AddNoteModal from './AddNoteModal';
import TicketsModal from './TicketsModal';
import NotesModal from './NotesModal';
import { useDispatch } from 'react-redux';
import { deleteCalendarNote, fetchCalendarData } from '../../../store/calendarSlice';
import { toast } from 'react-toastify';
import './CalendarView.scss';

const CalendarDay = ({ day, tasks, note, onTaskClick, activeMenuDate, setActiveMenuDate, onCreateTask, isWeekView = false, projectId, dateRange, filters, colorCodeOption }) => {
  const { t } = useTranslation();
  const { date, isCurrentMonth, isToday, dateString } = day;
  const dispatch = useDispatch();
  const menuOpen = activeMenuDate === dateString;
  const menuRef = useRef(null);
  const addIconRef = useRef(null);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [isTicketsModalOpen, setIsTicketsModalOpen] = useState(false);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  
  const notes = Array.isArray(note) ? note : (note ? [note] : []);

  useEffect(() => {
    if (!menuOpen) return;

    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) && 
          addIconRef.current && !addIconRef.current.contains(e.target)) {
        setActiveMenuDate(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen, setActiveMenuDate]);

  const handleClickAway = () => {
    if (menuOpen) {
      setActiveMenuDate(null);
    }
  };

  const handleMenuClick = (e) => {
    e.stopPropagation();
    setActiveMenuDate(menuOpen ? null : dateString);
  };

  const handleMenuItemClick = (action) => {
    setActiveMenuDate(null);
    
    if (action === 'Create Task' && onCreateTask) {
      onCreateTask(dateString);
    } else if (action === 'Add Note') {
      setSelectedNote(null);
      setIsNoteModalOpen(true);
    }
  };

  const handleNoteEdit = (note) => {
    setSelectedNote(note);
    setIsNotesModalOpen(false);
    setIsNoteModalOpen(true);
  };

  const handleNoteDelete = async (note) => {
    try {
      await dispatch(deleteCalendarNote({ noteId: note.id })).unwrap();
      toast.success(t('calendar.noteDeletedSuccess'));
      
      if (dateRange) {
        dispatch(fetchCalendarData({ 
          projectId, 
          fromDate: dateRange.fromDate, 
          toDate: dateRange.toDate, 
          filters 
        }));
      }
    } catch (error) {
      toast.error(error || t('calendar.failedToDeleteNote'));
    }
  };

  return (
    <Box 
      className={`${isWeekView ? 'calendar-day-week' : 'calendar-day'} ${!isCurrentMonth ? 'calendar-day-disabled' : ''} ${isToday ? 'calendar-day-today' : ''}`}
    >
      <Typography className="calendar-day-number">
        {date}
      </Typography>

      {notes.length > 0 && (
        <Tooltip 
          title={
            <Box className="calendar-note-tooltip-content">
              <Typography className="calendar-note-text">
                {notes[0].text}
              </Typography>
              {notes[0].assignee && notes[0].assignee.name && (
                <Box className="calendar-note-field">
                  <Person className="calendar-note-icon" />
                  <Typography className="calendar-note-label">
                    <strong>{t('calendar.assignee')}</strong> {notes[0].assignee.name}
                  </Typography>
                </Box>
              )}
              {notes[0].linkedTicket && notes[0].linkedTicket.title && (
                <Box className="calendar-note-field">
                  <LinkIcon className="calendar-note-icon" />
                  <Typography className="calendar-note-label">
                    <strong>{t('calendar.linkedTicket')}</strong> {notes[0].linkedTicket.ticket_number && `${notes[0].linkedTicket.ticket_number} `}{notes[0].linkedTicket.title}
                  </Typography>
                </Box>
              )}
              {notes.length > 1 && (
                <Typography className="calendar-note-label" sx={{ mt: 1, fontStyle: 'italic' }}>
                  +{notes.length - 1} {notes.length - 1 > 1 ? t('calendar.moreNotes') : t('calendar.moreNote')}
                </Typography>
              )}
            </Box>
          } 
          arrow 
          placement="top"
          slotProps={{ 
            tooltip: { className: 'app-tooltip' }
          }}
        >
          <Box 
            className="calendar-note-icon-wrapper"
            onClick={(e) => {
              e.stopPropagation();
              setIsNotesModalOpen(true);
            }}
          >
            <Badge 
              badgeContent={notes.length > 1 ? notes.length : 0} 
              color="primary"
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '10px',
                  height: '16px',
                  minWidth: '16px',
                  padding: '0 4px'
                }
              }}
            >
              <StickyNote2 className="calendar-note-icon-main" />
            </Badge>
          </Box>
        </Tooltip>
      )}

      <Box 
        ref={addIconRef}
        className={`calendar-day-add-icon ${menuOpen ? 'calendar-day-add-icon-active' : ''}`}
        onClick={handleMenuClick}
      >
        <Add />
      </Box>

      {menuOpen && (
        <Popper 
          open={menuOpen} 
          anchorEl={addIconRef.current} 
          placement="bottom"
          modifiers={[
            {
              name: 'flip',
              enabled: true,
              options: {
                fallbackPlacements: ['top', 'bottom', 'left', 'right'],
              },
            },
            {
              name: 'preventOverflow',
              enabled: true,
              options: {
                boundary: 'viewport',
              },
            },
          ]}
          style={{ zIndex: 1300 }}
        >
          <ClickAwayListener onClickAway={handleClickAway}>
            <Box className="calendar-day-menu" ref={menuRef}>
              <Box 
                className="calendar-day-menu-item"
                onClick={() => handleMenuItemClick('Create Task')}
              >
                <Assignment sx={{ fontSize: 20 }} />
                <span>{t('calendar.createTask')}</span>
              </Box>
              <Box 
                className="calendar-day-menu-item"
                onClick={() => handleMenuItemClick('Add Note')}
              >
                <StickyNote2 sx={{ fontSize: 20 }} />
                <span>{t('calendar.addNote')}</span>
              </Box>
            </Box>
          </ClickAwayListener>
        </Popper>
      )}
      
      <Box className="calendar-day-tasks">
        {tasks.slice(0, 2).map(task => (
          <CalendarTask
            key={task.id}
            task={task}
            onClick={() => onTaskClick(task)}
            colorCodeOption={colorCodeOption}
          />
        ))}
        {tasks.length > 2 && (
          <Typography 
            className="calendar-day-more"
            onClick={(e) => {
              e.stopPropagation();
              setIsTicketsModalOpen(true);
            }}
            sx={{ cursor: 'pointer' }}
          >
            +{tasks.length - 2} {t('calendar.more')}
          </Typography>
        )}
      </Box>

      <AddNoteModal
        open={isNoteModalOpen}
        onClose={() => {
          setIsNoteModalOpen(false);
          setSelectedNote(null);
        }}
        projectId={projectId}
        dateRange={dateRange}
        filters={filters}
        note={selectedNote}
        noteDate={dateString}
        mode={selectedNote ? 'edit' : 'create'}
      />

      <TicketsModal
        open={isTicketsModalOpen}
        onClose={() => setIsTicketsModalOpen(false)}
        tickets={tasks}
        date={dateString}
        colorCodeOption={colorCodeOption}
      />

      <NotesModal
        open={isNotesModalOpen}
        onClose={() => setIsNotesModalOpen(false)}
        notes={notes}
        date={dateString}
        onEdit={handleNoteEdit}
        onDelete={handleNoteDelete}
      />
    </Box>
  );
};

export default CalendarDay;
